import { createRouter, createWebHistory } from 'vue-router';
import { loadCurrentUser } from '@/entities/user/api/currentUserApi';
import { accueilPasse } from '@/pages/welcome/lib/skipped';
import {
  administersAnything,
  administersProject,
  administersTenant
} from '@/entities/user/lib/administration';

/**
 * Routes are lazy by design: the administration is a separate context the
 * majority of users never enter, and it should not weigh on the first paint of
 * the project list.
 */
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      component: () => import('@/app/layouts/AppLayout.vue'),
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/pages/home/ui/HomePage.vue')
        },
        {
          path: 'projets/:projectId',
          name: 'project',
          component: () => import('@/pages/project/ui/ProjectPage.vue')
        },
        {
          // Découvrir une solution qu'on n'a pas : dans l'application, avec un retour. Par
          // identifiant et non par adresse — la page met son contenu dans un cadre.
          path: 'decouvrir/:applicationId',
          name: 'solution',
          component: () => import('@/pages/solution/ui/SolutionPage.vue'),
          // La page produit occupe toute la zone : c'est un site entier dans un cadre, et
          // la gouttière de lecture du reste de l'application ne lui sert à rien.
          meta: { fullBleed: true }
        }
      ]
    },
    {
      // The perimeter is a path segment: every administration screen is scoped to one
      // organisation, so a link that does not carry it is a link to nothing.
      path: '/administration/:orgId',
      component: () => import('@/app/layouts/AdminLayout.vue'),
      meta: { requiresAdministration: true },
      children: [
        { path: '', redirect: { name: 'admin-projects' } },
        {
          path: 'projets',
          name: 'admin-projects',
          component: () => import('@/pages/admin/projects/ui/ProjectsPage.vue')
        },
        {
          path: 'projets/:projectId',
          name: 'admin-project',
          component: () => import('@/pages/admin/project/ui/ProjectAdminPage.vue')
        },
        {
          path: 'utilisateurs',
          name: 'admin-users',
          component: () => import('@/pages/admin/users/ui/UsersPage.vue')
        },
        {
          path: 'groupes',
          name: 'admin-groups',
          component: () => import('@/pages/admin/groups/ui/GroupsPage.vue')
        },
        {
          path: 'classifications',
          name: 'admin-classification',
          component: () => import('@/pages/admin/classification/ui/ClassificationPage.vue')
        }
      ]
    },
    {
      // Entering without one: the layout picks the first organisation the person
      // administers and rewrites the URL.
      path: '/administration',
      name: 'admin',
      component: () => import('@/pages/admin/entry/ui/AdminEntry.vue'),
      meta: { requiresAdministration: true }
    },
    {
      /*
       * L'accueil d'un invité, **hors de** `AppLayout` : il n'a ni organisation à choisir,
       * ni liste de projets, ni en-tête qui lui parle. Il a une carte et trois champs.
       *
       * L'identifiant est un segment de chemin et non un paramètre de requête : Keycloak
       * compare l'URI de retour entière aux motifs déclarés par le client, et filtre
       * certains paramètres au passage. C'est là que ça casserait en production.
       */
      path: '/bienvenue/:invitationId?',
      name: 'welcome',
      component: () => import('@/pages/welcome/ui/WelcomePage.vue')
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/not-found/ui/NotFoundPage.vue')
    }
  ]
});

/**
 * L'administration se refuse, elle ne se cache pas.
 *
 * Le bouton de l'en-tête ne s'affiche plus pour qui n'administre rien, mais un bouton
 * caché n'est pas une autorisation : l'adresse scopée tapée à la main ouvrait l'écran, qui
 * appelait l'API et obtenait ses listes (IDALL-4192). La garde est ici, avant que le
 * composant n'existe, et elle pose la question sur **l'organisation nommée** — administrer
 * la sienne ne donne rien sur celle du voisin, comme au backend.
 *
 * Le refus renvoie à l'accueil plutôt qu'à une page « interdit » : la personne n'a rien
 * demandé d'autre que ce qu'on lui a proposé, et l'accueil est ce qu'elle a.
 */
/**
 * Le second filet de l'accueil.
 *
 * L'invité arrive normalement par `/bienvenue/{id}`, que Keycloak lui a donné. Mais il a pu
 * cliquer le lien sur son téléphone et ouvrir l'application sur son portable, fermer
 * l'onglet, ou avoir été invité avant que cet écran existe : dans tous ces cas l'identifiant
 * n'est nulle part, et sans cette garde il arrive sur une liste de projets sous un nom
 * d'affichage qui est son adresse.
 *
 * `onboarding.required` est calculé par le backend — jamais connecté, ou prénom et nom
 * vides. Le « passer » de l'écran vaut pour la session : qui veut entrer doit pouvoir entrer.
 */
router.beforeEach(async (to) => {
  if (to.name === 'welcome' || accueilPasse()) return true;
  try {
    const me = await loadCurrentUser();
    if (me.onboarding?.required === true) {
      const invitation = me.onboarding.invitationId;
      return invitation ? { name: 'welcome', params: { invitationId: invitation } } : { name: 'welcome' };
    }
  } catch {
    // L'identité n'a pas répondu : on ne détourne personne sur une supposition.
  }
  return true;
});

router.beforeEach(async (to) => {
  if (to.meta.requiresAdministration !== true) return true;

  const orgId = typeof to.params.orgId === 'string' ? to.params.orgId : undefined;
  const projectId = typeof to.params.projectId === 'string' ? to.params.projectId : undefined;
  try {
    const me = await loadCurrentUser();
    // L'administrateur d'un seul projet passe par l'écran de ce projet, et par lui seul :
    // il n'administre pas l'organisation qui le porte, et les listes de celle-ci lui sont
    // fermées côté API comme côté écran.
    const allowed = orgId
      ? administersTenant(me, orgId) || administersProject(me, projectId)
      : administersAnything(me);
    if (allowed) return true;
  } catch {
    // L'identité n'a pas répondu : on ne devine pas dans le sens permissif.
  }
  return { name: 'home' };
});

export default router;
